import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import NodeSkeleton from "./NodeSkeleton";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { type NodeProps } from "@xyflow/react";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import D365ExistsIcon from "./D365ExistsIcon";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import NodeListCard from "./NodeListCard";
import NodeListRow, { NodeRowChevron } from "./NodeListRow";
import NodeToolbar from "./NodeToolbar";

function splitPascalCase(value: string): string {
    return value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

function RouteOperationsListNode({ data }: NodeProps<ExplorerNode>) {
    const operations = data.routeOperations ?? [];
    const isLoading = data.status === "loading";
    const isError = data.status === "error";
    const nodeColor = getNodeColor(data.entityType);

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            source={data.source}
            minWidth={240}
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                dataAreaId={data.dataAreaId}
                onInfoClick={data.selectId ? () => data.onSelect?.(data.selectId!) : undefined}
                titleAdornment={data.d365Exists !== undefined ? <D365ExistsIcon exists={data.d365Exists} /> : undefined}
            />

            {data.routeVersionSiteId && (
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                opacity: 0.8
                            }}>Site</Typography>
                        <Typography variant="caption" sx={{
                            fontWeight: 500
                        }}>{data.routeVersionSiteId}</Typography>
                    </Stack>
                </Stack>
            )}

            {isLoading ? (
                <NodeSkeleton rows={3} variant="list" />
            ) : isError ? (
                <Typography variant="caption" color="error">
                    Could not load operations from D365
                </Typography>
            ) : operations.length === 0 ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    No operations found
                </Typography>
            ) : (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {operations.map((op, index) => (
                        // operationId is the operation's unique name. operationNumber
                        // is not always unique in D365 data (we've seen duplicates),
                        // so don't use it as the React key. Append index as a final
                        // disambiguator in case operationId itself ever repeats.
                        (<React.Fragment key={`${op.operationId}-${index}`}>
                            {index > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                            <NodeListRow
                                nodeColor={nodeColor}
                                isExpanded={op.isExpanded}
                                onClick={() => data.onOperationSelect?.(op.operationNumber)}
                            >
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <Chip
                                        label={op.operationNumber}
                                        size="small"
                                        sx={{
                                            height: 16,
                                            minWidth: 24,
                                            fontSize: "0.65rem",
                                            fontWeight: 700,
                                            backgroundColor: `${nodeColor}20`,
                                            color: nodeColor,
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 400,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {splitPascalCase(op.operationId)}
                                    </Typography>
                                    {op.operationPriority > 0 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "text.secondary",
                                                fontSize: "0.65rem",
                                            }}
                                        >
                                            P{op.operationPriority}
                                        </Typography>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    <NodeRowChevron isExpanded={op.isExpanded} nodeColor={nodeColor} />
                                </Stack>
                            </NodeListRow>
                        </React.Fragment>)
                    ))}
                </List>
            )}
        </NodeListCard>
    );
}

export default memo(RouteOperationsListNode);
